import {
  Grid,
  GridColumn,
  GridDataStateChangeEvent,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { ReactElement, useEffect, useRef, useState } from "react";
import useIdentityStore from "../../../Zustand/identityStore";
import { Button } from "@progress/kendo-react-buttons";
import { useLocalization } from "@progress/kendo-react-intl";
import { enMessages } from "../../../messages/en-US";
import { AlertReadTableDto } from "../../../Models/Alerts/AlertReadTableDto";
import {
  CompositeFilterDescriptor,
  DataResult,
  FilterDescriptor,
  SortDescriptor,
  State,
  process,
} from "@progress/kendo-data-query";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import DeleteDialog from "../../Shared/Components/Dialogs/DeleteDialog";
import EntityNotesButton from "../../Dashboards/Assets/Components/Notes/EntityNotesButton";
import { Tooltip } from '@progress/kendo-react-tooltip';
import moment from "moment";
import { isInvalidDate } from "../../Shared/Utilities/DateFormattingUtils";
import { InputClearValue, TextBox, InputSeparator, TextBoxProps, Checkbox } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";

import React from "react";
import { 
  downloadIcon, 
  checkIcon, 
  xIcon,
  chevronDoubleUpIcon,
  chevronUpIcon,
  equalIcon,
  chevronDownIcon, 
  blurIcon,
  trackChangesAcceptIcon,
  pencilIcon,
  infoCircleIcon,
  arrowRotateCcwIcon,
  gaugeRadialIcon,
  trashIcon,
  dropletSliderIcon,
} from '@progress/kendo-svg-icons';
import { SvgIcon } from "@progress/kendo-react-common";
import { MultiSelect, MultiSelectChangeEvent } from "@progress/kendo-react-dropdowns";
import { faTowerBroadcast } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface Props {
  onClose: () => void;
  alertToUpdateHandler: (alert: any) => void;
  alerts: AlertReadTableDto[];
  deleteAlert: (alertId: string) => void;
  onDelete: (alert) => void;
  resetAlert: (alertId: string, alert: any) => void;
}

interface Item {
  text: string;
  fields: string[]; 
}

const ColumnsOptions: Item[] = [
  { text: 'Alert', fields: ["name", "description"] },
  { text: 'Assigned To', fields: ["assignedTo.name", "assignedTo.details"] },
  { text: 'Criticality', fields: ["criticality"] },
  { text: 'Type', fields: ["type"] }
];

export default function AlertsTable(props: Props): ReactElement {
  const currentUserRole = useIdentityStore((x) => x.user?.profile.role);
  const localizationService = useLocalization();
  const [alertToDelete, setAlertToDelete] = useState<AlertReadTableDto | null>(
    null
  );

  const initialSort: Array<SortDescriptor> = [
    {field:"alertStatus.isTriggered", dir:"desc"},
    { field: "criticalityId", dir: "asc" },
  ];
  const [sort] = useState(initialSort);

  const _export = useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  const alerts = props.alerts;

  //Creating dataState to prevent issues with virtual scrolling
  const createDataState = (dataState: State) => {
    return {
      result: process(alerts.slice(0), dataState),
      dataState: dataState,
    };
  };

  let initialState = createDataState({
    take: 30,
    skip: 0,
    sort: sort,
  });


  const [result, setResult] = useState<DataResult>(initialState.result);
  const [dataState, setDataState] = useState<State>(initialState.dataState);


  const dataStateChange = (event: GridDataStateChangeEvent) => {
    let updatedState = createDataState(event.dataState);
    setResult(updatedState.result);
    setDataState(updatedState.dataState);
  };

  const AlertCell = (props) => {
    return (
      <td>
        <div>{props.dataItem.name}</div>
        <div className="text-xs">{props.dataItem.description}</div>
      </td>
    );
  };

  const EnabledCell = (props) => {
    return (
      <td>
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '26px',
          height: '26px'
        }}>
          <SvgIcon icon={props.dataItem.enabled ? checkIcon : xIcon} size="large"/>
        </span>
      </td>
    );
  };

  const CriticalityCell = (props) => {
    let item = props.dataItem;
    const colour =
      item.criticality === "Critical"
        ? "#780000"
        : item.criticality === "High"
        ? "#dc0000"
        : item.criticality === "Medium"
        ? "#fd8c00"
        : "#fdc500";
    const icon =
      item.criticality === "Critical"
        ? chevronDoubleUpIcon
        : item.criticality === "High"
        ? chevronUpIcon
        : item.criticality === "Medium"
        ? equalIcon
        : chevronDownIcon;
    return (
      <td>
        <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
          <span
            style={{
              color: colour,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              width: '26px',
              height: '26px'
            }}
            title={item.criticality}
          >
            <SvgIcon icon={icon} size="large"/>
          </span>
        </Tooltip>
      </td>
    );
  };

  const TriggeredCell = (props) => {
    const formatOrDash = (date: string | undefined) =>
      isInvalidDate(date) ? " -" : moment(date).format(" DD/MM/YYYY @ HH:mm:ss");

    const lastEvaluated = formatOrDash(props.dataItem.alertStatus.lastEvaluated);
    const lastNotified = formatOrDash(props.dataItem.alertStatus.lastNotified);
    const lastReset = formatOrDash(props.dataItem.alertStatus.lastReset);
    const lastTriggered = formatOrDash(props.dataItem.alertStatus.lastTriggered);

    const TooltipContentTemplate = (props: any) => {
      return (
        <div>
          <div>
            {localizationService.toLanguageString("custom.lastEvaluated",enMessages.custom.lastEvaluated
            )}
            : {lastEvaluated}
          </div>
          <div>
            {localizationService.toLanguageString("custom.lastNotified",enMessages.custom.lastNotified
            )}
            : {lastNotified}
          </div>
          <div>
            {localizationService.toLanguageString("custom.lastReset",enMessages.custom.lastReset
            )}
            : {lastReset}
          </div>
          <div>
            {localizationService.toLanguageString("custom.lastTriggered",enMessages.custom.lastTriggered
            )}
            : {lastTriggered}
          </div>
        </div>
      );
    };

    return (
      <td>
        <Tooltip
          position={"bottom"}
          anchorElement={"target"}
          content={(props) => <TooltipContentTemplate title={props.title} />}
          parentTitle={true}
        >
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            width: '26px',
            height: '26px'
          }} title="Triggered">
            <SvgIcon icon={props.dataItem.alertStatus.isTriggered ? checkIcon : xIcon} size="large"/>
          </span>
        </Tooltip>
      </td>
    );
  };

  const TypeCell = (props) => {
    return (
      <td>
        <Tooltip position={"bottom"} anchorElement={"target"} parentTitle={true}>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            width: '26px',
            height: '26px'
          }} title={`${props.dataItem.type === "Reservoir" ? 
            localizationService.toLanguageString("custom.reservoir", enMessages.custom.reservoir) : 
            props.dataItem.type === "Pressure" ?
            localizationService.toLanguageString("custom.pressure", enMessages.custom.pressure) :
            props.dataItem.type === "Communication" ?
            localizationService.toLanguageString("custom.communications", enMessages.custom.communications) :
            props.dataItem.type === "Grease" ?
            localizationService.toLanguageString("custom.grease", enMessages.custom.grease) :
            localizationService.toLanguageString("custom.requirement", enMessages.custom.requirement)}
            ${localizationService.toLanguageString("custom.alert", enMessages.custom.alert)}`}>
            {props.dataItem.type === "Communication" ? (
              <FontAwesomeIcon icon={faTowerBroadcast} size="lg" />
            ) : (
              <SvgIcon 
                icon={props.dataItem.type === "Reservoir" ? 
                  dropletSliderIcon : 
                  props.dataItem.type === "Pressure" ? 
                  gaugeRadialIcon : 
                  props.dataItem.type === "Grease" ?
                  blurIcon :
                  trackChangesAcceptIcon} 
                size="large"
              />
            )}
          </span>
        </Tooltip>
      </td>
    );
  };

  const AssignedToCell = (props) => {
    return (
      <td>
        <div>{props.dataItem.assignedTo.name}</div>
        <div className="text-xs">{props.dataItem.assignedTo.details}</div>
      </td>
    );
  }

  const HeaderAssignedToCell = (props) => {
    const TooltipContent = () => (
      <div className="p-2">
        <div className="font-bold underline mb-1">
          {localizationService.toLanguageString("custom.communication", enMessages.custom.communication)} {localizationService.toLanguageString("custom.alert", enMessages.custom.alert)}
        </div>
        <div className="mb-1">
          {localizationService.toLanguageString("custom.endpoint", enMessages.custom.endpoint)} ({localizationService.toLanguageString("custom.serialNumber", enMessages.custom.serialNumber)})
        </div>
        <div>
          <div className="font-bold underline mb-1">{localizationService.toLanguageString("custom.requirement", enMessages.custom.requirement)} {localizationService.toLanguageString("custom.alert", enMessages.custom.alert)} or {localizationService.toLanguageString("custom.pressure", enMessages.custom.pressure)} {localizationService.toLanguageString("custom.alert", enMessages.custom.alert)}</div>
         <div>{localizationService.toLanguageString("custom.endpoint", enMessages.custom.endpoint)} ({localizationService.toLanguageString("custom.serialNumber", enMessages.custom.serialNumber)})</div>
         <div>{localizationService.toLanguageString("custom.location", enMessages.custom.location)} &gt; {localizationService.toLanguageString("custom.asset", enMessages.custom.asset)}</div>
        </div>
      </div>
    );

    return (
      <span onClick={props.onClick} className={'k-cell-inner'}>
        <span className={'k-link'}>
          <span className={'k-column-title'}>{props.title}</span>{' '}
          <Tooltip 
            anchorElement="target" 
            position="bottom"
            parentTitle={true}
            content={TooltipContent}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                marginLeft: '0.75rem',
                width: '26px',
                height: '26px'
              }}
              title=" "
            >
              <SvgIcon icon={infoCircleIcon} />
            </span>
          </Tooltip>
          {props.children}
        </span>       
      </span>
    );
  };

  const HeaderCriticalityCell = (props) => {
    const TooltipContent = () => (
      <div className="p-2">
        <div className="flex items-center gap-2">
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '26px',
              height: '26px'
            }}
          >
            <SvgIcon icon={chevronDoubleUpIcon} />
          </span>
          <span>: {localizationService.toLanguageString("custom.critical",enMessages.custom.critical)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '26px',
              height: '26px'
            }}
          >
            <SvgIcon icon={chevronUpIcon} />
          </span>
          <span>: {localizationService.toLanguageString("custom.high",enMessages.custom.high)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '26px',
              height: '26px'
            }}
          >
            <SvgIcon icon={equalIcon} />
          </span>
          <span>: {localizationService.toLanguageString("custom.medium",enMessages.custom.medium)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '26px',
              height: '26px'
            }}
          >
            <SvgIcon icon={chevronDownIcon} />
          </span>
          <span>: {localizationService.toLanguageString("custom.low",enMessages.custom.low)}</span>
        </div>
      </div>
    );

    return (
      <span onClick={props.onClick} className={'k-cell-inner'}>
        <span className={'k-link'}>
          <span className={'k-column-title'}>{props.title}</span>{' '}
          <Tooltip 
            anchorElement="target" 
            position="bottom"
            parentTitle={true}
            content={TooltipContent}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                marginLeft: '0.75rem',
                width: '26px',
                height: '26px'
              }}
              title=" "
            >
              <SvgIcon icon={infoCircleIcon} />
            </span>
          </Tooltip>
          {props.children}
        </span>       
      </span>
    );
  };

  const HeaderTypeCell = (props) => {
    const TooltipContent = () => (
      <div className="p-2">
        <div className="flex items-center gap-2">
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '26px',
              height: '26px'
            }}
          >
            <SvgIcon icon={dropletSliderIcon} />
          </span>
          <span>: {localizationService.toLanguageString("custom.reservoir", enMessages.custom.reservoir)} {localizationService.toLanguageString("custom.alert", enMessages.custom.alert)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '26px',
              height: '26px'
            }}
          >
            <SvgIcon icon={gaugeRadialIcon} />
          </span>
          <span>: {localizationService.toLanguageString("custom.pressure", enMessages.custom.pressure)} {localizationService.toLanguageString("custom.alert", enMessages.custom.alert)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '26px',
              height: '26px'
            }}
          >
            <SvgIcon icon={trackChangesAcceptIcon} />
          </span>
          <span>: {localizationService.toLanguageString("custom.requirement", enMessages.custom.requirement)} {localizationService.toLanguageString("custom.alert", enMessages.custom.alert)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '26px',
              height: '26px'
            }}
          >
            <FontAwesomeIcon icon={faTowerBroadcast} />
          </span>
          <span>: {localizationService.toLanguageString("custom.communications", enMessages.custom.communications)} {localizationService.toLanguageString("custom.alert", enMessages.custom.alert)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '26px',
              height: '26px'
            }}
          >
            <SvgIcon icon={blurIcon} />
          </span>
          <span>: {localizationService.toLanguageString("custom.grease", enMessages.custom.grease)} {localizationService.toLanguageString("custom.alert", enMessages.custom.alert)}</span>
        </div>
      </div>
    );

    return (
      <span onClick={props.onClick} className={'k-cell-inner'}>
        <span className={'k-link'}>
          <span className={'k-column-title'}>{props.title}</span>{' '}
          <Tooltip 
            anchorElement="target" 
            position="bottom"
            parentTitle={true}
            content={TooltipContent}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                marginLeft: '0.75rem',
                width: '26px',
                height: '26px'
              }}
              title=" "
            >
              <SvgIcon icon={infoCircleIcon} />
            </span>
          </Tooltip>
          {props.children}
        </span>       
      </span>
    );
  };


  // Columns for the table
  const columns: Array<any> = [
    {
      field: "name",
      title: `${localizationService.toLanguageString("custom.alert",enMessages.custom.alert)}`,
      cell: AlertCell,
      show: true,
    },
    {
      field: "assignedTo.name",
      title: `${localizationService.toLanguageString("custom.assignedTo",enMessages.custom.assignedTo)}`,
      show: true,
      cell: AssignedToCell,
      headerCell: HeaderAssignedToCell
    },
    {
      field: "criticalityId",
      title: `${localizationService.toLanguageString("custom.criticality",enMessages.custom.criticality)}`,
      show: true,
      width: 170,
      cell: CriticalityCell,
      headerCell: HeaderCriticalityCell
    },
    {
      field: "type",
      title: `${localizationService.toLanguageString("custom.type",enMessages.custom.type)}`,
      cell: TypeCell,
      width: 150,
      show: true,
      headerCell: HeaderTypeCell
    },

    {
      field: "enabled",
      title: `${localizationService.toLanguageString("custom.enabled",enMessages.custom.enabled)}`,
      cell: EnabledCell,
      show: true,
      width: 150,
    },
    {
      field: "alertStatus.isTriggered",
      title: localizationService.toLanguageString("custom.triggered", enMessages.custom.triggered),
      cell: TriggeredCell,
      show: true,
      width: 150,
    },
  ];

  useEffect(() => {
    let updatedState = createDataState(dataState);
    setResult(updatedState.result);
    setDataState(updatedState.dataState);
  }, [props.alerts]);

  const onDelete = () => {
    if (alertToDelete != null) {
      props.onDelete(alertToDelete);
    }

    setAlertToDelete(null);
  };

  const getExcelExportData = () => {
    return process(props.alerts, {
      filter: dataState.filter,
      sort: dataState.sort,
    }).data;
  };

  const [fieldsData, setFieldsData] = useState<string[]>(["name", "description", "assignedTo.name", "assignedTo.details", "criticality", "type"]);
  const [searchText, setSearchText] = useState<TextBoxProps["value"]>("");
  const [value, setValue] = useState<Item[]>(ColumnsOptions);
  
  useEffect(() => {
  
    if (searchText !== undefined && searchText !== null) {
      let filters: FilterDescriptor[] = [];

      fieldsData.map((field) => filters.push({field: field, operator: "contains", value: searchText}))

      let compositeFilter: CompositeFilterDescriptor = {
        logic: "or",
        filters: filters,
      };

      var newState = dataState; 
      newState.filter = compositeFilter;

      let updatedState = createDataState(newState);
      setResult(updatedState.result);
      setDataState(updatedState.dataState);
    }

   }, [fieldsData, searchText]);

  const handleFieldChange = (event: MultiSelectChangeEvent) => {
    const values = event.target.value;
        
    var selectedFields : string[] = []; 

    values.map((selectedItem) => {
      return selectedItem.fields?.map((field) => {
        return selectedFields.push(field);
      })
    })
      setFieldsData(selectedFields);
      setValue(values)
      
  };

  const handleSeachChange = React.useCallback((event) => setSearchText(event.target.value), []);

  const handleClear = React.useCallback(() => {
    setSearchText("");
  }, []);

  const renderItemWithCheckbox = (li: React.ReactElement, itemProps: any) => {
    const itemChildren = (
      <span>
        <Checkbox
          name={itemProps.dataItem.value}
          checked={itemProps.selected}
          onChange={() => {
            // Create a synthetic event that matches what the MultiSelect expects
            const syntheticEvent = {
              stopPropagation: () => {},
              preventDefault: () => {},
              target: { checked: !itemProps.selected }
            };
            itemProps.onClick(itemProps.index, syntheticEvent);
          }}
        >
          <Label className="k-checkbox-label">&nbsp;{itemProps.dataItem.text}</Label>
        </Checkbox>
      </span>
    );
    return React.cloneElement(li, li.props, itemChildren);
  };

  return (
    <div>
      <ExcelExport
        data={getExcelExportData()}
        ref={_export}
        collapsible={true}
        fileName={localizationService.toLanguageString("custom.alerts", enMessages.custom.alerts)}
      >
        <ExcelExportColumn field="name" title={localizationService.toLanguageString("custom.alert", enMessages.custom.alert)} />
        <ExcelExportColumn field="description" title={localizationService.toLanguageString("custom.description", enMessages.custom.description)} />
        <ExcelExportColumn field="assignedTo.name" title={localizationService.toLanguageString("custom.assignedTo", enMessages.custom.assignedTo)} />
        <ExcelExportColumn field="assignedTo.details" title={`${localizationService.toLanguageString("custom.location", enMessages.custom.location)} > ${localizationService.toLanguageString("custom.asset", enMessages.custom.asset)}`} />
        <ExcelExportColumn field="criticality" title={localizationService.toLanguageString("custom.criticality", enMessages.custom.criticality)} />
        <ExcelExportColumn field="type" title={localizationService.toLanguageString("custom.type", enMessages.custom.type)} />
        <ExcelExportColumn field="enabled" title={localizationService.toLanguageString("custom.enabled", enMessages.custom.enabled)} />
        <ExcelExportColumn field="alertStatus.isTriggered" title={localizationService.toLanguageString("custom.triggered", enMessages.custom.triggered)} />
      </ExcelExport>

      <Grid
        key={"AlertsTable"}
        data={result}
        scrollable={"virtual"}
        take={30}
        rowHeight={50}
        onDataStateChange={dataStateChange}
        {...dataState}
        sortable={{
          mode: "multiple",
        }}
        style={{
          height: "calc(100vh - 200px)",
        }}
      >
        <GridToolbar className="">
        
          <div className="w-full flex justify-between items-center">
            <div>
              <TextBox placeholder="Search..." onChange={handleSeachChange} style={{width:300, height: 40}}
              value={searchText} 
              suffix={() => (
                <React.Fragment>
                        {searchText !== "" && (
                          <InputClearValue onClick={handleClear}>
                            <SvgIcon icon={xIcon} />
                          </InputClearValue>
                        )}
                        <InputSeparator />
                      </React.Fragment>
              )} />
              <MultiSelect
                data={ColumnsOptions}
                textField="text"
                onChange={handleFieldChange}
                value={value}
                size={"large"}
                itemRender={renderItemWithCheckbox}
                autoClose={false}
                popupSettings={{ className: 'my-multiselect-popup' }}
                style={{width:470, overflow:"auto", marginLeft: "5px", height: 38}}
                className="multi-select-custom-display-readonly"
                
              />
            </div>
            {currentUserRole !== "GeneralUser" && (
              <Tooltip anchorElement="target" position="bottom">
                <Button
                  title={localizationService.toLanguageString('custom.exportToExcel', enMessages.custom.exportToExcel)}
                  onClick={excelExport}
                  themeColor={"base"}
                  svgIcon={downloadIcon}
                  style={{ marginRight: "10px" }}
                />
              </Tooltip>
            )}
          </div>
        </GridToolbar>

        <GridColumn
          title={localizationService.toLanguageString("custom.notes", enMessages.custom.notes)}
          width="65px"
          sortable={false}
          filterable={false}
          cell={(cellProps) => (
            <td className="text-center">
              <EntityNotesButton
                entityType="Alert"
                entityId={String(cellProps.dataItem.id)}
                entityName={cellProps.dataItem.name}
                entitySerial={cellProps.dataItem.assignedTo?.name}
                buttonProps={{ fillMode: "flat" }}
              />
            </td>
          )}
        />

        {columns.map(
          (column, idx) =>
            column.show && (
              <GridColumn
                key={idx}
                field={column.field}
                title={column.title}
                filter={column.filter}
                cell={column.cell}
                width={column.width}
                filterable={column.filterable}
                headerCell={column.headerCell}
              />
            )
        )}

        {currentUserRole !== "GeneralUser" && (
          <GridColumn
            title={localizationService.toLanguageString(
              "custom.actions",
              enMessages.custom.actions
            )}
            width="150px"
            sortable={false}
            filterable={false}
            cell={(cellProps) => {
              return (
                <td style={{textAlign: 'center'}}>
                  <Button
                    className="mr-1"
                    themeColor={"primary"}
                    svgIcon={pencilIcon}
                    onClick={async () => {
                      props.alertToUpdateHandler(cellProps.dataItem.id);
                    }}
                    title={localizationService.toLanguageString(
                      "custom.edit",
                      enMessages.custom.edit
                    )}
                  />
                  <Button
                    className=""
                    svgIcon={trashIcon}
                    themeColor={"primary"}
                    onClick={async () => {
                      setAlertToDelete(cellProps.dataItem);
                    }}
                    title={localizationService.toLanguageString(
                      "custom.delete",
                      enMessages.custom.delete
                    )}
                  />
                   <Button
                    className="ml-1"
                    svgIcon={arrowRotateCcwIcon}
                    themeColor={"primary"}
                    disabled={!cellProps.dataItem.alertStatus.isTriggered}
                    onClick={async () => {
                      props.resetAlert(cellProps.dataItem.id, cellProps.dataItem);
                    }}
                    title={"Reset"}
                  />
                </td>
              );
            }}
          />
        )}
      </Grid>

      {alertToDelete != null && (
        <DeleteDialog
          title={localizationService.toLanguageString(
            "custom.deleteAlert",
            enMessages.custom.deleteAlert
          )}
          deletingItemLabel={alertToDelete?.name}
          itemCount={0}
          open={alertToDelete != null}
          onClose={() => setAlertToDelete(null)}
          deleteCallback={onDelete}
          children={undefined}
        />
      )}
    </div>
  );
}
